import { useEffect, useState } from "react";
import { Mail, ShieldCheck, ShieldAlert, RefreshCw, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const statusStyle = (s) => s === "verified" ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/50 dark:text-green-300"
  : s === "pending" ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300"
  : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300";

const copy = (v) => navigator.clipboard?.writeText(v).then(() => toast.success("Copied"));

export const EmailDelivery = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/email/status").then((r) => setSt(r.data)).catch(() => setSt(false)); }, []);

  const verify = async (id) => {
    setBusy(id);
    try { const r = await api.post(`/email/domains/${id}/verify`); setSt(r.data); toast.success(r.data.test_mode ? "Verification requested — DNS can take a few minutes" : "Domain verified! Emails now send from your domain."); }
    catch (e) { toast.error(errMsg(e), { duration: 9000 }); } finally { setBusy(""); }
  };

  if (st === null) return <p className="text-sm text-muted-foreground">Checking email delivery...</p>;
  if (st === false) return <p className="text-sm text-muted-foreground">Email status unavailable.</p>;

  return (
    <div className="space-y-4 min-w-0" data-testid="email-delivery-section">
      <div className={`flex items-start gap-3 rounded-lg border p-4 ${st.test_mode ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" : "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20"}`}>
        {st.test_mode ? <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" /> : <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />}
        <div className="text-sm min-w-0">
          <div className="font-semibold" data-testid="email-sender-status">{st.test_mode ? "Test mode — emails only reach the app owner's inbox" : "Live — emails reach any client"}</div>
          <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap"><Mail className="h-3.5 w-3.5 shrink-0" /> Sending as <span className="font-mono text-foreground break-all" data-testid="email-sender-address">{st.sender}</span> · replies go to your business email</div>
        </div>
      </div>

      {st.domains.length === 0 && st.is_admin && (
        <p className="text-sm text-muted-foreground">No domain added yet. Add one at <a className="underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a>, then come back here for the DNS records.</p>
      )}

      {st.domains.map((d) => (
        <div key={d.name} className="rounded-lg border border-border overflow-hidden min-w-0" data-testid={`email-domain-${d.name.replace(/\W/g, "")}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-secondary/50">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono font-semibold truncate">{d.name}</span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusStyle(d.status)}`} data-testid={`email-domain-status-${d.name.replace(/\W/g, "")}`}>{d.status?.replace("_", " ")}</span>
            </div>
            {st.is_admin && d.status !== "verified" && (
              <Button size="sm" data-testid={`email-domain-verify-${d.name.replace(/\W/g, "")}`} disabled={busy === d.id} onClick={() => verify(d.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy === d.id ? "animate-spin" : ""}`} /> {d.status === "not_started" ? "I've added the DNS records" : "Check again"}
              </Button>
            )}
          </div>
          {st.is_admin && d.status !== "verified" && d.records?.length > 0 && (
            <div className="p-4 space-y-3">
              <p className="text-sm text-muted-foreground">Add these DNS records at your domain registrar (GoDaddy, Namecheap, Cloudflare…), then click the button above. Propagation usually takes 5–30 minutes.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-muted-foreground uppercase tracking-wider"><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Name / Host</th><th className="py-1 pr-3">Value</th><th className="py-1">Status</th></tr></thead>
                  <tbody>
                    {d.records.map((r, i) => (
                      <tr key={i} className="border-t border-border align-top">
                        <td className="py-2 pr-3 font-mono font-bold">{r.type}{r.priority ? ` (prio ${r.priority})` : ""}</td>
                        <td className="py-2 pr-3 font-mono break-all"><button type="button" onClick={() => copy(r.name)} className="inline-flex items-center gap-1 hover:text-emerald-600 text-left">{r.name} <Copy className="h-3 w-3 shrink-0" /></button></td>
                        <td className="py-2 pr-3 font-mono break-all max-w-[200px] sm:max-w-[320px]"><button type="button" onClick={() => copy(r.value)} className="inline-flex items-start gap-1 hover:text-emerald-600 text-left"><span className="break-all">{r.value.length > 60 ? r.value.slice(0, 60) + "…" : r.value}</span> <Copy className="h-3 w-3 shrink-0 mt-0.5" /></button></td>
                        <td className="py-2"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${statusStyle(r.status)}`}>{r.status?.replace("_", " ")}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">Manage in Resend <ExternalLink className="h-3 w-3" /></a>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
